import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/theme';
import ProductDetail from '../components/product/ProductDetail';
import ProductCard from '../components/product/ProductCard';
import { mockProducts } from '../utils/mockData';
import { Product } from '../utils/types';
import PageHero from '../components/layout/PageHero';
import { Container, Section } from '../styles/components';

const PageContainer = styled.div`
  padding-bottom: ${theme.spacing.xl};
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.darkGray};
  margin-bottom: ${theme.spacing.md};
  text-decoration: none;
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const NotFound = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} 0;
  
  h2 {
    margin-bottom: ${theme.spacing.md};
  }
  
  p {
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.darkGray};
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  margin: ${theme.spacing.xl} 0 ${theme.spacing.lg};
  font-family: ${theme.fonts.heading};
  position: relative;
  padding-bottom: ${theme.spacing.sm};
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const RelatedProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

interface RouteParams {
  id: string;
}

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<keyof RouteParams>() as RouteParams;
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // In a real application, you would fetch the product from an API
    // Here we're using mock data
    const foundProduct = mockProducts.find(p => p.id === id);
    setProduct(foundProduct || null);
    setLoading(false);
    
    if (foundProduct) {
      // Find related products (same category, but not the same product)
      const related = mockProducts
        .filter(p => p.category === foundProduct.category && p.id !== foundProduct.id)
        .slice(0, 4);
      setRelatedProducts(related);
    }
  }, [id]);
  
  if (loading) {
    return (
      <PageContainer>
        <PageHero 
          title="Loading Product"
          subtitle="Please wait while we fetch the product details"
          height="30vh"
        />
        <Section>
          <Container>
            <div>Loading product details...</div>
          </Container>
        </Section>
      </PageContainer>
    );
  }
  
  if (!product) {
    return (
      <PageContainer>
        <PageHero 
          title="Product Not Found"
          subtitle="The product you're looking for doesn't exist or has been removed"
          height="30vh"
        />
        <Section>
          <Container>
            <NotFound>
              <h2>Product Not Found</h2>
              <p>The product you're looking for doesn't exist or has been removed.</p>
              <Link to="/products">Go to Products</Link>
            </NotFound>
          </Container>
        </Section>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <PageHero 
        title={product.name}
        subtitle={product.category}
        height="30vh"
      />
      <Section>
        <Container>
          <BackButton to="/products">
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Products
          </BackButton>
          
          <ProductDetail product={product} />
          
          {relatedProducts.length > 0 && (
            <>
              <SectionTitle>Related Products</SectionTitle>
              <RelatedProductsGrid>
                {relatedProducts.map(relatedProduct => (
                  <ProductCard key={relatedProduct.id} product={relatedProduct} />
                ))}
              </RelatedProductsGrid>
            </>
          )}
        </Container>
      </Section>
    </PageContainer>
  );
};

export default ProductDetailPage; 